import React, { useState, useEffect } from 'react';
import styles from './Slides.module.scss'
import Media from '../../../partials/Media';
import Dialog from '../../../partials/Modal/Dialog';
import Questions from '../Questions';

const Slides = ({ slides, dialog, feedback, onComplete }) => {

    const [currentSlide, setCurrentSlide ] = useState()
    const [showSlidesDialog, setShowSlidesDialog ] = useState()
    const [showQuestionsDialog, setShowQuestionsDialog] = useState()
    const [currentQuestion, setCurrentQuestion ] = useState(0)
    const [viewedSlides, setViewedSlides] = useState([])
    const [askedQuestions, setAskedQuestions] = useState([])
    const [readedMessages, setReadedMessages] = useState([])

    /**
     * If the dialog is opened outside this piece
     */
    useEffect(() => {
        setViewedSlides([])
        setCurrentQuestion(0)
        setShowSlidesDialog(dialog)
    }, [dialog])


    useEffect(() => {

    if(currentSlide){
        const introduction  = { 
            participant: currentSlide.presenter, 
            message: currentSlide.introduction,
            button: "Next",
            action: () => startSlides(currentSlide)
        }
        feedback([introduction])
    }


    }, [currentSlide])


    /**
     * Start participant conversation
     * for the slides, there are two options
     * the slide starts with a conversation
     * or it starts with questions
     * @param {obj} slide 
     */
    const startSlides = (slide) => {

        if(slide.conversation) {
            startConversation(slide.conversation)
        }
        else if(slide.questions) {
            startQuestions(slide.questions)
        }
        else {
            setShowSlidesDialog(true)
        }
    }
    
     const startConversation = (conversation) =>{
        
        if(conversation.length > 0){
            
            nextMessage(conversation)
        }
        else{
            startQuestions()
        }
    }

    const getPendingMessages = (conversation, messageId=false) => {

        const lastReaded = readedMessages.reverse()[0]

        for( var i = 0; i < conversation.length; i++){ 
            
            if ( conversation[i].id === lastReaded || conversation[i].id === messageId) { 
                conversation.splice(0, i+1); 
            }
        }
        return conversation

    }

    const nextMessage = (conversation) => {

        const message = conversation[0]
        markAsRead(message.id)

        const feedbackWithAction = conversation.map((message, index) => {
            
            const isLast = (index + 1 === conversation.length)
            const hasQuestion =  message.question ?? false

            return {
                ...message, 
                action: 
                    hasQuestion ? () => startQuestions(message.question) : 
                    isLast ? ()=> nextQuestion(message.id) : false,
                markAsRead: () => markAsRead(message.id)
            }
        })
        
        feedback(feedbackWithAction)
    }

    /**
     * startQuestions if the question prop of
     * the conversation is an actual id, 
     * the question will be asigned, otherwise
     * it will use the first question of the array,
     * @param {string} questionId 
     */
    const startQuestions = (id) => {
        
        const questionById = findQuestionById(id, currentSlide.questions)
        askQuestionFeedback( questionById ?? currentSlide.questions[0] )
        setCurrentQuestion(questionById ?? currentSlide.questions[0])
    }

    /**
     * if the question has question prop 
     * for it, it will display the bubbles 
     * for the participant and will attach
     * setShowQuestionsDialog to pop when we hit next
     * otherwise will set showQuestionsDialog to true
     * and display the first question
     * 
     * @param {*} question 
     */
    const askQuestionFeedback = (question) => {
        /**
         * set this question as already asked
         */
        setAskedQuestions([...askedQuestions, question.id])

        if(question.question){
            const askQuestion  = { 
                participant: question.participant, 
                message: question.question,
                button: "Answer Question",
                action: () => setShowQuestionsDialog(true)
            }
            feedback([askQuestion])
        }
        else{
            setShowQuestionsDialog(true)
        }
    }

    const markAsRead = (message) =>{
        setReadedMessages([...readedMessages, message]) 
    }

    /**
     * Search for a question by an id 
     * if added in the editor
     * @param {bool | string} id 
     * @param {arr} questions 
     * @returns a question object
     */
    const findQuestionById = (id, questions) => {
        return questions.filter(question => question.id === id)[0]
    }

    /**
     * Validation for questions asked
     * @param {*} id 
     * @returns 
     */
     const isQuestionAsked = (id) => {
        return askedQuestions.includes(id)
    }

    /**
     * Validation for viewed slides
     * @param {*} id 
     * @returns 
     */
    const isSlideViewed = (id) => {
        return viewedSlides.find(slide => slide === id) ?? false
    }

    /**
     * Handles slide selection of the dialog box
     * data structure requires a presenter and a introduction
     * to start the slide presentation
     * @param {*} id 
     * @returns 
     */
    const handleSlideSelection = (id) =>{

        if(isSlideViewed(id)) return false

        const slide = slides.filter((slide) => slide.id === id)[0]

        setCurrentSlide(slide)
        setViewedSlides([...viewedSlides, id])
        setShowSlidesDialog(false)
        
    }   

    /**
     * Handles nextQuestion if theres any left
     * if not will pop the dialog box if there are any slides left
     * else will end the program and show a continue button to end
     * the interaction
     */
    const nextQuestion = (lastMessageId=false) => {

        const questionsLeft = currentSlide.questions.filter(question => !isQuestionAsked(question.id))
        const conversationPending = getPendingMessages(currentSlide.conversation, lastMessageId);
        
        if(conversationPending.length > 0 ) {
            startConversation(conversationPending)
        }
        else if(questionsLeft.length > 0) {
            startQuestions(questionsLeft[0].id)
        }
        else if(slides.length === viewedSlides.length){
            endSlides()
        }
        else {
            setCurrentQuestion(0)
            setAskedQuestions([])
            setReadedMessages([]) 
            setShowSlidesDialog(true)
        }
        
    }

    /**
     * handles each time a question is answered, 
     * every question has to have a feedback object 
     * in the conversation data structure
     * { message: string, participant: int|string }
     * @param {obj} questionFeedback 
     */
    const handleQuestionFeedback = (questionFeedback) => {
        
        const feedbackWithActions = questionFeedback.map((conversation, index) => {

            const isLast = (index + 1 === questionFeedback.length)
            return {
                ...conversation, 
                action: isLast ? ()=> nextQuestion() : false
            }

        })

        setShowQuestionsDialog(false)
        feedback(feedbackWithActions)
        
    }

    const endSlides = () => {
        setShowQuestionsDialog(false)
        setCurrentSlide()
        onComplete()
    }
    return (
    <>
    
        <div className={styles.Slides}>
            <div className={styles.Projection} >
                {currentSlide && <Media src={currentSlide.media.src} alt={currentSlide.name} /> }
                {currentSlide && <span>Click to Expand</span>}
            </div>
           <p> {currentSlide && currentSlide.name} </p>
            
        </div>

    
        {showSlidesDialog &&
                <Dialog show={true} header="Choose Presentation Slide">
                    {
                        slides.map((slide) => { 

                            const viewed = isSlideViewed(slide.id)

                            return (
                                <div 
                                    key={slide.id}
                                    className={`${styles.slideOption} ${viewed ? styles.viewed : ''} `} 
                                    onClick={()=>handleSlideSelection(slide.id)}>
                                    {slide.name}
                                </div>
                            )
                        })
                    }

                </Dialog>
            }
        
            {currentSlide && 
                <Dialog show={showQuestionsDialog}>
                    <Questions 
                        question={currentQuestion} 
                        onComplete={handleQuestionFeedback} 
                        feedback={handleQuestionFeedback} 
                    />
                </Dialog>
            }
    </>
    )
    
}

export default Slides;